import { observable, action } from 'mobx';
import { makePersistable } from 'mobx-persist-store';
import { DisposableBase } from '../Types';

export interface EventTheme {
    id: string;
    name: string;
    message: string;
    speechMessage?: string; // Optional speech-specific message
    eventClassification: string;
    color: {
        r: number;
        g: number;
        b: number;
        a: number;
    };
    deniesAccess?: boolean; // Guard Mode: matched + this theme => "No Access", no IN/OUT scan
}

export class EventThemesStore extends DisposableBase {
    @observable accessor themes: EventTheme[] = [
        {
            id: 'default-recognized',
            name: 'Recognized',
            message: 'Person Recognized',
            speechMessage: 'Access granted. Welcome.',
            eventClassification: 'recognized',
            color: { r: 34, g: 197, b: 94, a: 0.8 }, // Green
        },
        {
            id: 'default-unknown',
            name: 'Unknown',
            message: 'Unknown Person',
            speechMessage: 'Unknown person detected.',
            eventClassification: 'unknown',
            color: { r: 239, g: 68, b: 68, a: 0.8 }, // Red
        },
        {
            id: 'default-no-match',
            name: 'No Match',
            message: 'No Match Found',
            speechMessage: 'No match found.',
            eventClassification: 'no-match',
            color: { r: 156, g: 163, b: 175, a: 0.8 }, // Gray
        },
    ];

    constructor() {
        super();
        // Configure persistence for themes
        makePersistable(this, {
            name: 'EventThemesStore',
            properties: ['themes'],
            storage: localStorage,
        }).catch(console.error);
    }

    @action addTheme = (theme: Omit<EventTheme, 'id'>) => {
        const newTheme: EventTheme = {
            ...theme,
            id: `theme-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        };
        this.themes.push(newTheme);
        this.saveToStorage(); // Ensure persistence after adding
    };

    @action updateTheme = (id: string, updates: Partial<Omit<EventTheme, 'id'>>) => {
        const themeIndex = this.themes.findIndex((t) => t.id === id);
        if (themeIndex !== -1) {
            this.themes[themeIndex] = { ...this.themes[themeIndex], ...updates };
            this.saveToStorage(); // Ensure persistence after updating
        }
    };

    @action removeTheme = (id: string) => {
        this.themes = this.themes.filter((t) => t.id !== id);
        this.saveToStorage(); // Ensure persistence after removing
    };

    @action duplicateTheme = (id: string) => {
        const theme = this.themes.find((t) => t.id === id);
        if (theme) {
            this.addTheme({
                name: `${theme.name} (Copy)`,
                message: theme.message,
                speechMessage: theme.speechMessage,
                eventClassification: theme.eventClassification,
                color: { ...theme.color },
                deniesAccess: theme.deniesAccess,
            });
        }
    };

    private saveToStorage = () => {
        // Force persistence to localStorage immediately
        try {
            localStorage.setItem('EventThemesStore', JSON.stringify({ themes: this.themes }));
            console.log('EventThemesStore: Themes saved to localStorage');
        } catch (error) {
            console.error('EventThemesStore: Failed to save themes to localStorage:', error);
        }
    };

    getThemeByClassification(classification: string): EventTheme | null {
        const match = this.themes.find((theme) => theme.eventClassification === classification);
        if (match) return match;
        // Catch-all fallback so an unrecognized classification still surfaces an overlay
        // (proves the pipeline is working). Caller can override by adding a configured theme.
        return {
            id: '__fallback__',
            name: 'Unknown Classification',
            message: classification,
            eventClassification: classification,
            color: { r: 156, g: 163, b: 175, a: 0.8 }, // Neutral gray
        };
    }

    getThemeById(id: string): EventTheme | null {
        return this.themes.find((theme) => theme.id === id) || null;
    }

    getColorString = (theme: EventTheme): string => {
        const { r, g, b, a } = theme.color;
        return `rgba(${r}, ${g}, ${b}, ${a})`;
    };

    getColorStringWithAlpha = (theme: EventTheme, alpha?: number): string => {
        const { r, g, b } = theme.color;
        const finalAlpha = alpha !== undefined ? alpha : theme.color.a;
        return `rgba(${r}, ${g}, ${b}, ${finalAlpha})`;
    };

    @action replaceThemes = (newThemes: EventTheme[]) => {
        // Validate that each theme has required properties
        const validThemes = newThemes.filter((theme) => {
            return (
                theme.id &&
                theme.name &&
                theme.eventClassification &&
                theme.color &&
                typeof theme.color.r === 'number' &&
                typeof theme.color.g === 'number' &&
                typeof theme.color.b === 'number' &&
                typeof theme.color.a === 'number'
            );
        });

        if (validThemes.length > 0) {
            this.themes = validThemes;
            this.saveToStorage();
            console.log(`EventThemesStore: Replaced themes with ${validThemes.length} valid themes from URL config`);
        } else {
            console.warn('EventThemesStore: No valid themes found in URL config, keeping existing themes');
        }
    };

    @action resetToDefaults = () => {
        console.log('EventThemesStore: Resetting themes to defaults');
        const defaultThemes: EventTheme[] = [
            {
                id: 'default-recognized',
                name: 'Recognized',
                message: 'Person Recognized',
                speechMessage: 'Access granted. Welcome.',
                eventClassification: 'recognized',
                color: { r: 34, g: 197, b: 94, a: 0.8 }, // Green
            },
            {
                id: 'default-unknown',
                name: 'Unknown',
                message: 'Unknown Person',
                speechMessage: 'Unknown person detected.',
                eventClassification: 'unknown',
                color: { r: 239, g: 68, b: 68, a: 0.8 }, // Red
            },
            {
                id: 'default-no-match',
                name: 'No Match',
                message: 'No Match Found',
                speechMessage: 'No match found.',
                eventClassification: 'no-match',
                color: { r: 156, g: 163, b: 175, a: 0.8 }, // Gray
            },
        ];

        this.themes = defaultThemes;
        this.saveToStorage();
    };
}
